import { isEmpty } from 'lodash-es';
import Cookies from 'universal-cookie';

import { getSegmentSessionId } from '../Analytics/Segment/segment';
import { isEnvironmentLocal, isEnvironmentLocalAndDevelopment } from '../Environment/utils';
import { loggerUtils } from '../Logging/loggerUtils';
import { appendQueryParams } from '../UrlQueryParam/utils';

import {
  addErrorMessage,
  buildErrorMessageColorHub,
  getCsrfCookieName,
  getError,
  type IErrorPayloadWithOptionalMessage,
} from './api-util';
import { ApiError } from './ApiError';
import { NetworkError } from './NetworkError';
import type { IErrorPayload } from './types';

/**
 * A non-exhaustive list of HTTP request methods.
 */
export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

const COOKIES = new Cookies();

const ENABLE_SLOW_API = isEnvironmentLocal() && false;
if (ENABLE_SLOW_API && !isEnvironmentLocal()) {
  throw new Error('You are enabling slow api in non-dev environment!');
}

// // Debug code to bypass fetch response for a given endpoint.
// // @ts-ignore
// const oldFetch = global.fetch;
// // @ts-ignore
// global.fetch = async (url, ...args) => {
//   if (!url.includes('covid_benefits/eligibility')) { return oldFetch(url, ...args);}
//   console.error('blowing up')
//   return Promise.resolve({ok: false, status: 403, headers: {
//     get: () => '',
//   }});
// }

const NON_LOGGABLE_HTTP_ERROR_STATUSES = new Set([401]);

const X_CSRF_TOKEN_HEADER_KEY = 'X-CSRFToken';
const X_SEGMENT_SESSION_ID_HEADER_KEY = 'X-Segment-Session-Id';

/**
 * Add default request options.
 *
 * @param fetchOptions - The initial fetch options.
 */
function addDefaultFetchOptions(fetchOptions: RequestInit, url: string): RequestInit {
  const headers: HeadersInit &
    Partial<{
      [X_CSRF_TOKEN_HEADER_KEY]: string;
      [X_SEGMENT_SESSION_ID_HEADER_KEY]: number;
    }> = { ...fetchOptions.headers };
  const csrfCookieName = getCsrfCookieName();
  if (!('X-CSRFToken' in headers) && COOKIES.get(csrfCookieName)) {
    headers[X_CSRF_TOKEN_HEADER_KEY] = COOKIES.get(csrfCookieName);
  }

  if (!('X-Segment-Session-Id' in headers)) {
    headers[X_SEGMENT_SESSION_ID_HEADER_KEY] = getSegmentSessionId();
  }

  // Include credentials (session cookies) for local development
  let includeCredentials = isEnvironmentLocalAndDevelopment();
  const { origin } = window.location;
  let destination = '';
  try {
    destination = new URL(url).origin;
  } catch {
    // relative paths will throw since they're not valid URLs
  }

  if (origin.match(/.*hub.*\.color\.(com|dev)$/) && destination.match(/.*[./]color\.(com|dev)$/)) {
    // Include credentials (session cookies) when making requests from
    // ColorHub to the Clinical backend.
    includeCredentials = true;
  }

  return {
    ...fetchOptions,
    headers,
    credentials: includeCredentials ? 'include' : 'same-origin',
  };
}

interface ColorFetchOptions extends RequestInit {
  /* tslint:disable-next-line:no-any */
  body?: any;
  /* tslint:disable-next-line:no-any */
  headers?: any;
}

export interface ColorRequestOptions {
  // Any query params that should be passed through the request.
  params?: object;

  // Any JSON data that should be passed through the request.
  json?: object;

  // Any headers that should be passed through the request.
  headers?: object;

  body?: object;
}

interface FileUploadRequestOptions {
  // Any query params that should be passed through the request.
  params?: object;

  // File or FormData that should be passed through the request.
  file: File | FormData;

  // Any headers that should be passed through the request.
  headers?: { [key: string]: string };

  // Optional fetch method, defaults to `POST`
  method?: 'POST' | 'PUT';
}

// FIXME: Convert this to a plain object. This doesn't need to be a class...
class APIClient {
  private buildErrorMessage: (error: IErrorPayloadWithOptionalMessage) => IErrorPayload;

  constructor(buildErrorMessage: (error: IErrorPayloadWithOptionalMessage) => IErrorPayload) {
    this.buildErrorMessage = buildErrorMessage;
  }

  /**
   * A fetch wrapper, with an API similar to `requests` in Python.
   *
   * Helper methods which include the method are provided below.
   *
   * @param method - The HTTP request method.
   * @param url - The url to make a request to.
   * @param options - Additional information about this request.
   */
  public request = (
    method: Method,
    url: string,
    options: ColorRequestOptions = {}
  ): Promise<any> => {
    let fetchHeaders = options.headers;
    const { params = {}, json = {} } = options;

    const urlWithQueryParams = appendQueryParams(url, params);

    let body: object | undefined;
    const methodIsGet = method === 'GET';

    if (!isEmpty(json)) {
      if (!methodIsGet) {
        body = json;
      }
      fetchHeaders = {
        // FIXME - `Content-Type` doesn't make sense for `GET` requests.
        'Content-Type': 'application/json',
        // FIXME - `Accept` is unrelated to whether we are sending a JSON object.
        // Most likely all our API's should specify this, since our endpoints can
        // only really handle JSON (not HTML)
        Accept: 'application/json',
        ...fetchHeaders,
      };
    }

    const fetchOptions = {
      method,
      body,
      headers: fetchHeaders,
    };

    return this.colorFetch(urlWithQueryParams, fetchOptions);
  };

  public get = (url: string, options: ColorRequestOptions = {}): Promise<any> => {
    return this.request('GET', url, options);
  };

  public post = (url: string, options: ColorRequestOptions = {}): Promise<any> => {
    return this.request('POST', url, options);
  };

  public postFormData = (url: string, options: ColorRequestOptions = {}): Promise<any> => {
    // Bypass this.request which ignores body and adds JSON specific headers
    return this.colorFetch(url, { ...options, method: 'POST' });
  };

  public put = (url: string, options: ColorRequestOptions = {}): Promise<any> => {
    return this.request('PUT', url, options);
  };

  public delete = (url: string, options: ColorRequestOptions = {}): Promise<any> => {
    return this.request('DELETE', url, options);
  };

  public patch = (url: string, options: ColorRequestOptions = {}): Promise<any> => {
    return this.request('PATCH', url, options);
  };

  public uploadFile = (url: string, options: FileUploadRequestOptions): Promise<any> => {
    const { params = {}, headers, method = 'POST', file } = options;

    const urlWithQueryParams = appendQueryParams(url, params);

    const fetchOptions = {
      method,
      body: file,
      headers: {
        // This is necessary to receive a response in json format
        // Otherwise you will get this error: 'renderer returned unicode, and did not specify'
        Accept: 'application/json',
        ...headers,
      },
    };

    return this.doFetch(urlWithQueryParams, addDefaultFetchOptions(fetchOptions, url));
  };

  /**
   * A fetch wrapper.
   *
   * @param url - The url to make a request against
   * @param options - The fetch options
   */
  public colorFetch = (url: string, options: ColorFetchOptions): Promise<any> => {
    let fetchBody = options.body;

    if (typeof fetchBody === 'object' && !(fetchBody instanceof FormData)) {
      fetchBody = JSON.stringify(fetchBody);
    }

    return this.doFetch(
      url,
      addDefaultFetchOptions(
        {
          ...options,
          body: fetchBody,
        },
        url
      )
    );
  };

  // To fix this we'd have to make this all methods in this class static.

  private doFetch = async (url: string, options: RequestInit): Promise<any> => {
    const UNSPECIFIED_SERVER_ERROR: IErrorPayload = {
      message: 'Failed to process request.',
      status: 500,
    };
    let response: any;
    try {
      if (ENABLE_SLOW_API) {
        // Enable this to observe slow API responses in development.
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      response = await fetch(url, options);
    } catch (error) {
      // Do not log to sentry.
      loggerUtils.logToConsole(getError(error));
      // This only occurs for the following scenarios:
      // 1. Network failures like bad wifi
      // 2. Invalid hosts or domains, such as CORS requests
      // This will not catch for invalid endpoints like /random/endpoint since
      // (404 not found is a fetch success but not `responseIsOk`)
      return Promise.reject(new NetworkError('Request failed due to a network error.'));
    }
    try {
      const contentType =
        response.headers.get('Content-Type') || response.headers.get('content-type') || '';
      const isJsonContentType = contentType.includes('application/json');
      const { ok: responseIsOk, status } = response;
      // Reject responses that aren't a 200-299
      if (!responseIsOk) {
        let responseContent = UNSPECIFIED_SERVER_ERROR;
        if (isJsonContentType) {
          const errorResponse = await response.json();
          if (typeof errorResponse === 'object') {
            responseContent = this.buildErrorMessage(errorResponse);
          } else if (typeof errorResponse === 'string') {
            // JSON is a primitive string (in quotes)
            responseContent.message = errorResponse;
          }
        } else if (contentType.includes('application/text')) {
          // Plain text in body, with appropriate header.
          responseContent.message = await response.text();
        }
        responseContent.status = status;
        loggerUtils.logToConsole(
          new Error(`Server responded with ${status} for URL: ${url}`),
          responseContent
        );
        const apiError = new ApiError(responseContent);
        if (status >= 400 && !NON_LOGGABLE_HTTP_ERROR_STATUSES.has(status)) {
          loggerUtils.logException(apiError);
        }
        return Promise.reject(apiError);
      }
      // Only return JSON (a little bizarre - FIXME)
      if (isJsonContentType) {
        return response.json();
      }
      return null;
    } catch (error) {
      // This will occur when `response.json()` or `response.text()` throws.
      // Examples:
      // * response.json() attempts to parse non JSON (worth logging)
      // * response stream is interrupted during processing. (e.g. intermittent network) (probably not worth logging, but is likely low occurrence)
      loggerUtils.logException(getError(error));
      return Promise.reject(new ApiError(UNSPECIFIED_SERVER_ERROR));
    }
  };
}

// Export a singleton for convenience.
export const ApiClient: APIClient = new APIClient(addErrorMessage);
export const ColorHubApiClient: APIClient = new APIClient(buildErrorMessageColorHub);
